import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/auth/AuthContext';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCaption, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Check, X, Mail, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
// import { sendRecruiterVerificationResult } from '@/services/emailService';
import AdminLayout from '@/components/layouts/AdminLayout';
import { createClient } from '@supabase/supabase-js';

// Create a Supabase client with the service role key for admin operations
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SERVICE_ROLE_KEY = import.meta.env.VITE_SUPABASE_SERVICE_ROLE_KEY;

// Initialize service role client for admin operations
const serviceRoleClient = createClient(
  SUPABASE_URL,
  SERVICE_ROLE_KEY,
  {
    auth: {
      autoRefreshToken: false,
      persistSession: false
    }
  }
);

interface RecruiterVerificationProps {}

interface RecruiterToVerify {
  id: string;
  email: string;
  company_email: string | null;
  first_name: string;
  last_name: string;
  created_at: string;
  organization_name: string;
}

const RecruiterVerification: React.FC<RecruiterVerificationProps> = () => {
  const [recruiters, setRecruiters] = useState<RecruiterToVerify[]>([]);
  const [loading, setLoading] = useState(true);
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user is admin, if not redirect
    if (!isAdmin()) {
      toast.error('You do not have permission to access this page');
      navigate('/dashboard');
      return;
    }

    fetchRecruitersToVerify();
  }, [isAdmin, navigate]);

  const fetchRecruitersToVerify = async () => {
    try {
      setLoading(true);
      console.log('[ADMIN-DEBUG] Starting to fetch recruiters requiring verification');
      
      // Check if we can access the profiles table with service role client
      const { data: testData, error: testError } = await serviceRoleClient
        .from('profiles')
        .select('id')
        .limit(1);
        
      if (testError) {
        console.error('[ADMIN-DEBUG] Error testing access to profiles table:', testError);
        throw new Error('Database connection issue: ' + testError.message);
      }
      
      console.log('[ADMIN-DEBUG] Service role client connection successful, fetching profiles needing verification');
      
      // Get the list of profiles that need verification using service role client
      const { data: profilesData, error: profilesError } = await serviceRoleClient
        .from('profiles')
        .select(`
          id,
          first_name,
          last_name,
          company_email,
          created_at,
          user_role,
          organizations:organization_id (name)
        `)
        .eq('user_role', 'recruiter')
        .eq('needs_manual_verification', true);

      if (profilesError) {
        console.error('[ADMIN-DEBUG] Error fetching profiles:', profilesError);
        throw profilesError;
      }

      console.log('[ADMIN-DEBUG] Fetched profiles:', profilesData);
      
      // Fetch emails for each profile using the service role client
      const enhancedProfiles = await Promise.all(profilesData.map(async (profile) => {
        try {
          // Get user email from auth.users using admin API
          const { data: userData, error: userError } = await serviceRoleClient.auth.admin.getUserById(profile.id);
          
          if (userError || !userData) {
            console.error('[ADMIN-DEBUG] Error fetching user email for profile:', profile.id, userError);
            return { ...profile, email: "Email fetch failed" };
          }
          
          return { ...profile, email: userData.user?.email || "No email found" };
        } catch (error) {
          console.error('[ADMIN-DEBUG] Error in email fetch for profile:', profile.id, error);
          return { ...profile, email: "Error fetching email" };
        }
      }));
      
      console.log('[ADMIN-DEBUG] Enhanced profiles with emails:', enhancedProfiles);
      
      // Format the profiles for display
      const formattedRecruiters = enhancedProfiles.map(profile => {
        // Extract organization name safely
        let organizationName = 'Unknown Organization';
        if (profile.organizations) {
          if (typeof profile.organizations === 'object' && profile.organizations !== null) {
            organizationName = (profile.organizations as any)?.name || 'Unknown Organization';
          }
        }
        
        return {
          id: profile.id,
          email: profile.email || "Email not available",
          company_email: profile.company_email,
          first_name: profile.first_name || '',
          last_name: profile.last_name || '',
          created_at: profile.created_at || new Date().toISOString(),
          organization_name: organizationName
        };
      });

      console.log('[ADMIN-DEBUG] Formatted recruiters with emails:', formattedRecruiters);
      setRecruiters(formattedRecruiters);
    } catch (error: any) {
      console.error('[ADMIN-DEBUG] Error in recruiter verification process:', error);
      toast.error(`Failed to load recruiters: ${error.message || 'Unknown error'}`);
      setRecruiters([]);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (recruiterId: string) => {
    try {
      // Get recruiter details from the profiles table
      const { data: recruiter, error: fetchError } = await supabase
        .from('profiles')
        .select('first_name, last_name')
        .eq('id', recruiterId)
        .single();
      
      if (fetchError) {
        throw fetchError;
      }

      // Get the email directly using the admin API
      const { data: userData, error: userError } = await serviceRoleClient.auth.admin.getUserById(recruiterId);
      
      if (userError || !userData) {
        console.error('Error fetching user:', userError || 'No user data returned');
        throw new Error('Could not fetch user data');
      }

      // Extract email from the user data
      const userEmail = userData.user?.email;
      
      if (!userEmail) {
        throw new Error('User email not found');
      }
      
      console.log('[ADMIN-DEBUG] Updating profile with serviceRoleClient:', {
        recruiterId,
        update: {
          needs_manual_verification: false,
          verified_manually: true,
          verified_at: new Date().toISOString()
        }
      });
      
      // Update the profile using the service role client instead of regular supabase
      const { error } = await serviceRoleClient
        .from('profiles')
        .update({
          needs_manual_verification: false,
          verified_manually: true,
          verified_at: new Date().toISOString()
        })
        .eq('id', recruiterId);

      if (error) {
        console.error('[ADMIN-DEBUG] Error updating profile:', error);
        throw error;
      }
      
      console.log('[ADMIN-DEBUG] Profile updated successfully, sending email notification');
      
      // Send approval notification email
      const recruiterName = `${recruiter.first_name || ''} ${recruiter.last_name || ''}`.trim();
      // Email notification temporarily disabled
      // await sendRecruiterVerificationResult(
      //   recruiterName,
      //   userEmail,
      //   true // isApproved = true
      // );

      toast.success('Recruiter account approved');
      
      // Refresh the list
      fetchRecruitersToVerify();
    } catch (error) {
      console.error('Error approving recruiter:', error);
      toast.error('Failed to approve recruiter');
    }
  };

  const handleReject = async (recruiterId: string) => {
    try {
      // Get recruiter details from the profiles table
      const { data: recruiter, error: fetchError } = await supabase
        .from('profiles')
        .select('first_name, last_name')
        .eq('id', recruiterId)
        .single();
      
      if (fetchError) {
        throw fetchError;
      }

      // Get the email directly using the admin API
      const { data: userData, error: userError } = await serviceRoleClient.auth.admin.getUserById(recruiterId);
      
      if (userError || !userData) {
        console.error('Error fetching user:', userError || 'No user data returned');
        throw new Error('Could not fetch user data');
      }

      // Extract email from the user data
      const userEmail = userData.user?.email;
      
      if (!userEmail) {
        throw new Error('User email not found');
      }
      
      console.log('[ADMIN-DEBUG] Updating profile with serviceRoleClient:', {
        recruiterId,
        update: {
          needs_manual_verification: false,
          verified_manually: false,
          verification_rejected: true,
          rejected_at: new Date().toISOString()
        }
      });
      
      // Update the profile using the service role client instead of regular supabase
      const { error } = await serviceRoleClient
        .from('profiles')
        .update({
          needs_manual_verification: false,
          verified_manually: false,
          verification_rejected: true,
          rejected_at: new Date().toISOString()
        })
        .eq('id', recruiterId);

      if (error) {
        console.error('[ADMIN-DEBUG] Error updating profile:', error);
        throw error;
      }
      
      console.log('[ADMIN-DEBUG] Profile updated successfully, sending rejection email');
      
      // Send rejection notification email
      const recruiterName = `${recruiter.first_name || ''} ${recruiter.last_name || ''}`.trim();
      // Email notification temporarily disabled
      // await sendRecruiterVerificationResult(
      //   recruiterName,
      //   userEmail,
      //   false // isApproved = false
      // );

      toast.success('Recruiter account rejected');
      
      // Refresh the list
      fetchRecruitersToVerify();
    } catch (error) {
      console.error('Error rejecting recruiter:', error);
      toast.error('Failed to reject recruiter');
    }
  };

  const sendVerificationEmail = async (recruiterId: string, email: string, name: string) => {
    try {
      // Implementation would depend on your email service
      // For example, using your existing email service:
      
      // Create and send the email
      toast.success(`Verification email sent to ${email}`);
    } catch (error) {
      console.error('Error sending verification email:', error);
      toast.error('Failed to send verification email');
    }
  };

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-roundone-dark-purple mb-2">Recruiter Verification</h1>
          <p className="text-muted-foreground mb-6">Verify recruiters who signed up with personal email addresses</p>
        </div>
        <Card>
          <CardContent className="p-6">
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-800"></div>
              </div>
            ) : recruiters.length === 0 ? (
              <div className="text-center py-8">
                <AlertCircle className="mx-auto h-12 w-12 text-gray-400" />
                <p className="mt-4 text-gray-500">No recruiters require verification at this time</p>
              </div>
            ) : (
              <Table>
                <TableCaption>Recruiters requiring manual verification</TableCaption>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Organization</TableHead>
                    <TableHead>Registered</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recruiters.map((recruiter) => (
                    <TableRow key={recruiter.id}>
                      <TableCell>
                        {recruiter.first_name} {recruiter.last_name}
                      </TableCell>
                      <TableCell>
                        {recruiter.email}
                        <Badge variant="outline" className="ml-2 bg-yellow-50 text-yellow-700 border-yellow-200">
                          Personal Email
                        </Badge>
                      </TableCell>
                      <TableCell>{recruiter.organization_name}</TableCell>
                      <TableCell>{new Date(recruiter.created_at).toLocaleDateString()}</TableCell>
                      <TableCell>
                        <div className="flex space-x-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="bg-green-50 text-green-600 border-green-200 hover:bg-green-100"
                            onClick={() => handleApprove(recruiter.id)}
                          >
                            <Check className="h-4 w-4 mr-1" /> Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="bg-red-50 text-red-600 border-red-200 hover:bg-red-100"
                            onClick={() => handleReject(recruiter.id)}
                          >
                            <X className="h-4 w-4 mr-1" /> Reject
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="bg-blue-50 text-blue-600 border-blue-200 hover:bg-blue-100"
                            onClick={() => sendVerificationEmail(
                              recruiter.id, 
                              recruiter.email, 
                              `${recruiter.first_name} ${recruiter.last_name}`
                            )}
                          >
                            <Mail className="h-4 w-4 mr-1" /> Request Verification
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default RecruiterVerification; 